/**
 * フォーム回答者へのメール予約配信ダイアログ。
 * 配信予約 (ScheduleFormDialog) を「直接アドレス」モードで開き、
 * フォーム回答者のメアドを宛先に自動投入するラッパー。
 *
 * - フォームカード / 回収一覧画面の両方から再利用。
 * - submissions プロップ指定時はそのリストのみを宛先候補にする
 *   （回収一覧の検索・絞り込み適用後の表示中リストと宛先を一致させるため）。
 *   未指定時は useFormSubmissions(formId) で全回答者を取得。
 * - 呼び出し側は open のときのみマウントすること（formId 無指定で全件取得しないため）。
 * - 宛先はダイアログ上で編集・除外も可能。
 */
import { useMemo } from 'react';

import { useFormSubmissions, type FormSubmissionRecord } from '@/hooks/useForms';
import { ScheduleFormDialog } from '@/pages/mail/schedules/ScheduleFormDialog';

interface Props {
  formId: string;
  formName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** 宛先候補にする回答リスト。指定時はフェッチせずこのリストのみを使用（絞り込み連動用）。 */
  submissions?: FormSubmissionRecord[];
}

const EMAIL_RE = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

export function FormScheduleDialog({
  formId,
  formName,
  open,
  onOpenChange,
  submissions: submissionsProp,
}: Props) {
  // prop 指定時もフックは無条件呼び出し（回収一覧では同一キーのキャッシュ共有で追加フェッチなし）
  const { data: fetched } = useFormSubmissions(formId);
  const source = submissionsProp ?? fetched;

  // 回答者メアドを重複排除・正規化（小文字化）して宛先候補に。
  const emails = useMemo(() => {
    const seen = new Set<string>();
    for (const s of source ?? []) {
      const e = s.email?.trim().toLowerCase();
      if (e && EMAIL_RE.test(e)) seen.add(e);
    }
    return Array.from(seen);
  }, [source]);

  const contextHint = submissionsProp
    ? `「${formName}」の一覧に表示中の回答者 ${emails.length} 名のメアドを宛先に設定しました（検索・絞り込みの適用結果と同じです）。メール本文を選び、送信日時を指定して予約してください。宛先は下の「直接アドレス」欄で個別に追加・削除できます。`
    : `「${formName}」の回答者 ${emails.length} 名のメアドを宛先に設定しました。メール本文を選び、送信日時を指定して予約してください。宛先は下の「直接アドレス」欄で個別に追加・削除できます。`;

  return (
    <ScheduleFormDialog
      open={open}
      onOpenChange={onOpenChange}
      initialTargetType="addresses"
      initialAddresses={emails}
      contextHint={contextHint}
    />
  );
}
